import React, { useEffect, useState } from "react";
import api, { PLATFORM_META } from "../lib/api";
import { Loader2, Wand2, Save, MessageCircle, Phone, Rocket, Clock, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { AutopilotEngine } from "./AutopilotEngine";
import { TelegramSetup } from "./TelegramSetup";

const DEFAULT_RATIOS = { carousel: "4:5", viral: "1:1", static: "16:9" };

const inputCls = "w-full bg-slate-950/60 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-indigo-500/60 transition-colors";

const Toggle = ({ on, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick} className={`w-11 h-6 rounded-full p-0.5 transition-colors ${on ? "bg-indigo-500" : "bg-slate-700"}`}>
    <span className={`block w-5 h-5 rounded-full bg-white transition-transform ${on ? "translate-x-5" : ""}`} />
  </button>
);

export const AutopilotSettings = ({ onDone, onChanged }) => {
  const [s, setS] = useState(null);
  const [brands, setBrands] = useState([]);
  const [bestTimes, setBestTimes] = useState({});
  const [saving, setSaving] = useState(false);
  const [theme, setTheme] = useState("");
  const [count, setCount] = useState(5);
  const [planPlatforms, setPlanPlatforms] = useState(["linkedin"]);
  const [planBrand, setPlanBrand] = useState("");
  const [withImages, setWithImages] = useState(true);
  const [useBestTime, setUseBestTime] = useState(true);
  const [planning, setPlanning] = useState(false);

  const load = async () => {
    const [st, br, bt] = await Promise.all([api.get("/settings"), api.get("/brands"), api.get("/best-times")]);
    setS(st.data); setBrands(br.data); setBestTimes(bt.data);
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setS((cur) => ({ ...cur, [k]: v }));

  const [models, setModels] = useState([]);
  const [current, setCurrent] = useState("");
  const [ratios, setRatios] = useState([]);
  const [savedAt, setSavedAt] = useState(0);

  useEffect(() => {
    api.get("/image-models")
      .then(({ data }) => {
        setModels(data.models || []);
        setCurrent(data.current || "");
        setRatios(data.ratios || []);
      })
      .catch(() => { /* the pickers just stay empty */ });
    // Re-reads after a save so "renders as" reflects the newly chosen model.
  }, [savedAt]);

  const save = async () => {
    setSaving(true);
    try {
      const p = { ...s }; delete p.id;
      await api.put("/settings", p);
      setSavedAt(Date.now());   // re-reads the ratio preview for the saved model
      toast.success("Settings saved");
      onChanged && onChanged();
    }
    catch (err) {
      // The server's reason matters here: a plan that doesn't include autopilot
      // says so, rather than leaving the user guessing at "save failed".
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const togglePlan = (p) => setPlanPlatforms((c) => (c.includes(p) ? c.filter((x) => x !== p) : [...c, p]));

  const generatePlan = async () => {
    if (!theme.trim()) return toast.error("Enter a content theme");
    setPlanning(true);
    try {
      const { data } = await api.post("/autopilot/plan", {
        theme, count: Number(count), platforms: planPlatforms, brand_id: planBrand || null,
        with_images: withImages, best_time: useBestTime,
      });
      toast.success(`Created ${data.created.length} scheduled posts${withImages ? " — images generating…" : ""}`);
      onDone && onDone("calendar");
    } catch { toast.error("Plan generation failed"); } finally { setPlanning(false); }
  };

  if (!s) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-indigo-400" /></div>;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="autopilot-view">
      <div>
        <p className="text-xs font-semibold tracking-wider uppercase text-indigo-400 mb-2">Autopilot</p>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-50">Autopilot & Settings</h1>
      </div>

      <AutopilotEngine s={s} set={set} brands={brands} onSave={save} saving={saving}
        refreshBrands={() => api.get("/brands").then(({ data }) => setBrands(data))} />

      {/* Plan generator */}
      <div className="glass rounded-2xl p-6 space-y-4 relative overflow-hidden">
        <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-indigo-500/10 blur-2xl" />
        <div className="flex items-center gap-2"><Rocket className="w-4 h-4 text-indigo-300" /><h3 className="font-heading font-bold text-slate-100">One-off plan from a theme</h3></div>
        <p className="text-xs text-slate-400">Separate from the engine: draft a batch of posts around a theme you choose, dropped straight onto your calendar.</p>
        <input data-testid="plan-theme" value={theme} onChange={(e) => setTheme(e.target.value)} placeholder="Theme, e.g. B2B SaaS growth tips for founders" className={inputCls} />
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">Brand voice</label>
            <select data-testid="plan-brand" value={planBrand} onChange={(e) => setPlanBrand(e.target.value)} className={inputCls}>
              <option value="">No brand</option>
              {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">Number of posts</label>
            <input data-testid="plan-count" type="number" min={1} max={14} value={count} onChange={(e) => setCount(e.target.value)} className={inputCls} />
          </div>
        </div>
        <div>
          <label className="text-xs text-slate-400 mb-1.5 block">Platforms</label>
          <div className="flex gap-2">
            {Object.entries(PLATFORM_META).map(([k, m]) => {
              const on = planPlatforms.includes(k);
              return <button key={k} data-testid={`plan-platform-${k}`} onClick={() => togglePlan(k)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${on ? "text-white" : "border-slate-700 text-slate-400"}`}
                style={on ? { background: `${m.color}22`, borderColor: m.color, color: "#fff" } : {}}>{m.label}</button>;
            })}
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex items-center gap-3 flex-1 p-3 rounded-xl bg-slate-950/50 border border-slate-800">
            <ImageIcon className="w-4 h-4 text-indigo-300" />
            <div className="flex-1"><p className="text-sm font-medium text-slate-200">AI image per post</p><p className="text-[11px] text-slate-500">Auto-generate a visual for every post</p></div>
            <Toggle on={withImages} onClick={() => setWithImages(!withImages)} testid="toggle-with-images" />
          </div>
          <div className="flex items-center gap-3 flex-1 p-3 rounded-xl bg-slate-950/50 border border-slate-800">
            <Clock className="w-4 h-4 text-indigo-300" />
            <div className="flex-1"><p className="text-sm font-medium text-slate-200">Best-time scheduling</p><p className="text-[11px] text-slate-500">Pick top engagement slots per platform</p></div>
            <Toggle on={useBestTime} onClick={() => setUseBestTime(!useBestTime)} testid="toggle-best-time" />
          </div>
        </div>
        <button data-testid="generate-plan-btn" onClick={generatePlan} disabled={planning}
          className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold transition-colors disabled:opacity-60">
          {planning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} {planning ? "Planning..." : "Generate plan"}
        </button>
      </div>

      {/* Best time slots */}
      <div className="glass rounded-2xl p-6 space-y-4">
        <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-indigo-300" /><h3 className="font-heading font-bold text-slate-100">Recommended posting slots</h3></div>
        <div className="grid sm:grid-cols-3 gap-4">
          {Object.entries(bestTimes).map(([plat, slots]) => (
            <div key={plat}>
              <div className="flex items-center gap-2 mb-2"><span className="w-2 h-2 rounded-full" style={{ background: PLATFORM_META[plat]?.color }} /><p className="text-xs font-semibold text-slate-300">{PLATFORM_META[plat]?.label}</p></div>
              <div className="space-y-1.5">
                {slots.slice(0, 3).map((sl, i) => (
                  <div key={i} className="flex items-center justify-between text-xs px-2.5 py-1.5 rounded-lg bg-slate-950/50 border border-slate-800">
                    <span className="text-slate-300 font-mono">{sl.label}</span>
                    <span className="text-emerald-400 font-semibold">{sl.score}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <TelegramSetup />

      {/* WhatsApp */}
      <div className="glass rounded-2xl p-6 space-y-4">
        <div className="flex items-center gap-2"><Phone className="w-4 h-4" style={{ color: "#25D366" }} /><h3 className="font-heading font-bold text-slate-100">WhatsApp approval (Twilio)</h3></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="text-xs text-slate-400 mb-1.5 block">Account SID</label>
            <input data-testid="twilio-sid" value={s.twilio_account_sid || ""} onChange={(e) => set("twilio_account_sid", e.target.value)} placeholder="ACxxxx" className={inputCls} /></div>
          <div><label className="text-xs text-slate-400 mb-1.5 block">Auth Token</label>
            <input data-testid="twilio-token" value={s.twilio_auth_token || ""} onChange={(e) => set("twilio_auth_token", e.target.value)} placeholder="••••••" className={inputCls} /></div>
          <div><label className="text-xs text-slate-400 mb-1.5 block">WhatsApp From</label>
            <input data-testid="twilio-from" value={s.twilio_whatsapp_from || ""} onChange={(e) => set("twilio_whatsapp_from", e.target.value)} placeholder="whatsapp:+14155238886" className={inputCls} /></div>
          <div><label className="text-xs text-slate-400 mb-1.5 block">Send approvals to</label>
            <input data-testid="twilio-to" value={s.whatsapp_to || ""} onChange={(e) => set("whatsapp_to", e.target.value)} placeholder="whatsapp:+91..." className={inputCls} /></div>
        </div>
      </div>

      {/* Routing */}
      <div className="glass rounded-2xl p-6 space-y-4">
        <h3 className="font-heading font-bold text-slate-100">Approval routing</h3>
        <div className="flex gap-2">
          {[["telegram", "Telegram"], ["whatsapp", "WhatsApp"], ["both", "Both"]].map(([v, l]) => (
            <button key={v} data-testid={`routing-${v}`} onClick={() => set("approval_channel", v)}
              className={`px-4 py-2 rounded-xl text-sm font-semibold border transition-colors ${s.approval_channel === v ? "bg-indigo-500/15 border-indigo-500/50 text-indigo-200" : "border-slate-700 text-slate-400 hover:text-slate-200"}`}>{l}</button>
          ))}
        </div>
        <p className="text-xs text-slate-500">If a channel isn't configured, posts still land in the in-app Approvals queue.</p>
      </div>

      {/* Aspect ratio per format */}
      <div className="glass rounded-2xl p-6 space-y-5">
        <div>
          <h3 className="font-heading font-bold text-slate-100">Image shape</h3>
          <p className="text-xs text-slate-500 mt-1">
            The ratio each post format is generated at. The model renders the nearest
            shape it supports — shown under each option, so you can see when a choice
            cannot be matched exactly.
          </p>
        </div>
        {[
          ["carousel", "Carousel slides", "4:5 and 1:1 are the two LinkedIn and Instagram carousels support"],
          ["viral", "Viral posts", "Square reads best at thumbnail size"],
          ["static", "Static posts", "Landscape suits link previews"],
        ].map(([fmt, label, hint]) => (
          <div key={fmt}>
            <p className="text-sm font-semibold text-slate-200">{label}</p>
            <p className="text-[11px] text-slate-500 mb-2">{hint}</p>
            <div className="flex flex-wrap gap-2">
              {ratios.map((r) => {
                const active = (s[`${fmt}_ratio`] || DEFAULT_RATIOS[fmt]) === r.id;
                const exact = r.renders_as === r.id || r.renders_as.includes("x");
                return (
                  <button
                    key={r.id}
                    data-testid={`ratio-${fmt}-${r.id.replace(":", "-")}`}
                    onClick={() => set(`${fmt}_ratio`, r.id)}
                    title={r.label}
                    className={`px-3 py-2 rounded-xl border text-left transition-colors ${
                      active ? "bg-indigo-500/15 border-indigo-500/50" : "border-slate-700 hover:border-slate-600"
                    }`}
                  >
                    <span className="block text-sm font-semibold text-slate-100 font-mono">{r.id}</span>
                    <span className="block text-[10px] text-slate-500 font-mono">
                      {r.renders_as}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Image model */}
      <div className="glass rounded-2xl p-6 space-y-4">
        <h3 className="font-heading font-bold text-slate-100">Image model</h3>
        <p className="text-xs text-slate-500">
          Every format uses one house art direction — dark premium palette, cinematic lighting,
          no stock-AI clichés — so a carousel reads as one campaign. The model only changes the render.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {models.map((m) => (
            <button
              key={m.id}
              data-testid={`image-model-${m.id}`}
              onClick={() => set("image_model", m.id)}
              className={`text-left px-4 py-3 rounded-xl border transition-colors ${
                (s.image_model || current) === m.id
                  ? "bg-indigo-500/15 border-indigo-500/50"
                  : "border-slate-700 hover:border-slate-600"
              }`}
            >
              <p className="text-sm font-semibold text-slate-100 font-mono">{m.id}</p>
              <p className="text-[11px] text-slate-500 mt-0.5">{m.sizes.join(" · ")}</p>
            </button>
          ))}
        </div>
      </div>

      <button data-testid="save-settings-btn" onClick={save} disabled={saving}
        className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold transition-colors disabled:opacity-60">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save settings
      </button>
    </div>
  );
};
